import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  SimpleGrid, 
  Icon, 
  VStack,
  HStack,
  useBreakpointValue,
  chakra,
  Flex
} from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { 
  FaCode, 
  FaWater, 
  FaCoffee, 
  FaMobileAlt, 
  FaWrench,
  FaWifi,
  FaLeaf,
  FaRocket,
  FaBrain,
  FaShieldAlt
} from 'react-icons/fa';
import { COLORS } from '../../globalvariables';

const MotionBox = chakra(motion.div);
const MotionFlex = chakra(motion.div);

// Helper function to map icon names to components
const getIconComponent = (iconName) => {
  const iconMap = {
    'code': FaCode,
    'water': FaWater,
    'coffee': FaCoffee,
    'mobile': FaMobileAlt,
    'wrench': FaWrench,
    'wifi': FaWifi,
    'leaf': FaLeaf,
    'rocket': FaRocket,
    'brain': FaBrain,
    'shield': FaShieldAlt
  };
  
  return iconMap[iconName] || FaCoffee;
};

const FeatureShowcase = ({ title, description, features }) => {
  const controls = useAnimation();
  const containerRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          controls.start('visible');
        }
      },
      { threshold: 0.1 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0.5 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 150,
        damping: 10,
        delay: 0.2
      }
    },
    hover: {
      scale: 1.1,
      color: "#E1DBDB",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 8
      }
    }
  };

  return (
    <Box py={{ base: 8, md: 16 }} px={{ base: 2, md: 4 }} ref={containerRef}>
      <MotionBox
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        maxW="1200px"
        mx="auto"
      >
        <VStack spacing={{ base: 4, md: 6 }} mb={{ base: 8, md: 12 }} textAlign="center">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl" }}
            mb={2}
            bgGradient={`linear(to-r, ${COLORS.textPrimary}, ${COLORS.primary} 50%, ${COLORS.textPrimary})`}
            bgClip="text"
            fontWeight="bold"
          >
            {title}
          </Heading>
          
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={COLORS.textSecondary}
            maxW="800px"
          >
            {description}
          </Text>
        </VStack>

        <SimpleGrid columns={columns} spacing={{ base: 6, md: 10 }} mt={{ base: 6, md: 8 }}>
          {features.map((feature, index) => (
            <MotionBox 
              key={index}
              variants={itemVariants}
              role="group"
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.98 }}
              animate={{ y: 0 }}
              p={{ base: 4, md: 6 }}
              bg={COLORS.primary}
              borderRadius="xl"
              boxShadow="md"
              borderWidth="1px"
              borderColor="rgba(255,255,255,0.1)"
              transition="all 0.3s"
              _hover={{
                boxShadow: `0 0 20px ${COLORS.primary}80`,
                borderColor: `${COLORS.cardBackground}`
              }}
            >
              <HStack spacing={{ base: 3, md: 4 }} mb={{ base: 3, md: 4 }} align="flex-start">

              <MotionFlex
  whileTap={{ scale: 0.95 }}
  w={{ base: "50px", md: "60px" }}
  h={{ base: "50px", md: "60px" }}
  borderRadius="full"
  bg={COLORS.creamText}
  justify="center"
  align="center"
  border={`2px solid ${COLORS.cardBackground}`}
  boxShadow={`0 0 10px ${COLORS.primary}40`}
  display="flex"
  transition="transform 0.2s ease"
  _groupHover={{ transform: "scale(1.05)" }}
>
  <Icon
    as={getIconComponent(feature.icon)}
    boxSize={{ base: "24px", md: "28px" }}
    color={COLORS.primary}
    transition="color 0.3s ease"
    _groupHover={{ color: COLORS.textPrimary }} 
    sx={{
      display: "block",
      margin: "auto",
    }}
  />
</MotionFlex>
                
                <VStack align="flex-start" spacing={0}>
                  <Heading as="h3" size={{ base: "sm", md: "md" }} color={COLORS.creamText}>
                    {feature.title}
                  </Heading>
                  
                  {feature.subtitle && (
                    <Text color={COLORS.creamText} fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                      {feature.subtitle}
                    </Text>
                  )}
                </VStack>
              </HStack>
              
              <Text color={COLORS.creamText} fontSize={{ base: "sm", md: "md" }}>
                {feature.description}
              </Text>
            </MotionBox>
          ))}
        </SimpleGrid>
      </MotionBox>
    </Box>
  );
};

export default FeatureShowcase;